import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import PageAlert from "../../components/PageAlert/PageAlert";
import { resolveApiError } from "../../utils/apiError";
import {
  asBoolean,
  categoryThumbnailUrl,
  createCategory,
  fetchCategory,
  makeSlug,
  updateCategory,
} from "../../api/categoryApi";
import "./products.scss";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const emptyForm = {
  name: "",
  name_zh: "",
  slug: "",
  status: true,
  sort_order: 0,
  show_in_navigation_menu: true,
  thumbnail_image: null,
  thumbnail_url: "",
};

const formFromCategory = (category) => ({
  name: category?.name || "",
  name_zh: category?.name_zh || "",
  slug: category?.slug || "",
  status: category?.status !== false,
  sort_order: category?.sort_order ?? 0,
  show_in_navigation_menu: asBoolean(category?.show_in_navigation_menu, true),
  thumbnail_image: null,
  thumbnail_url: categoryThumbnailUrl(category),
});

const ProductCategoryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = useMemo(() => Boolean(id), [id]);
  const [form, setForm] = useState(emptyForm);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(location.state?.notice || "");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!location.state?.notice) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const loadCategory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const category = await fetchCategory(id);
      setForm(formFromCategory(category));
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) loadCategory();
  }, [isEdit, loadCategory]);

  useEffect(() => {
    if (!form.thumbnail_image) {
      setThumbnailPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(form.thumbnail_image);
    setThumbnailPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [form.thumbnail_image]);

  const updateField = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "name" && (!current.slug || current.slug === makeSlug(current.name))) {
        next.slug = makeSlug(value);
      }
      if (field === "slug") next.slug = makeSlug(value);
      return next;
    });
  };

  const onThumbnailChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      updateField("thumbnail_image", null);
      return;
    }
    if (file.type && !IMAGE_TYPES.includes(file.type)) {
      setError("Use JPG, PNG or WebP images only.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("Each image must be 5 MB or smaller.");
      event.target.value = "";
      return;
    }
    setError("");
    updateField("thumbnail_image", file);
  };

  const clearThumbnail = () => {
    setForm((current) => ({ ...current, thumbnail_image: null, thumbnail_url: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const uploadedFile = form.thumbnail_image;
      const removedThumbnail = !uploadedFile && !form.thumbnail_url;
      const payload = {
        name: form.name,
        name_zh: form.name_zh,
        slug: form.slug || makeSlug(form.name),
        status: form.status,
        sort_order: Number(form.sort_order) || 0,
        show_in_navigation_menu: form.show_in_navigation_menu,
        thumbnail_image: uploadedFile || (removedThumbnail ? null : undefined),
      };
      const saved = isEdit ? await updateCategory(id, payload) : await createCategory(payload);
      if (!isEdit) {
        navigate("/admin/products/categories", { replace: true, state: { notice: "Category created." } });
        return;
      }

      let category = saved && typeof saved === "object" ? saved : null;
      try {
        category = await fetchCategory(id);
      } catch {
        // Fall back to the update response if a follow-up fetch fails.
      }

      const next = formFromCategory({
        ...form,
        ...(category && typeof category === "object" ? category : {}),
      });
      if (uploadedFile && next.thumbnail_url) {
        const joiner = next.thumbnail_url.includes("?") ? "&" : "?";
        next.thumbnail_url = `${next.thumbnail_url}${joiner}t=${Date.now()}`;
      } else if (uploadedFile && !next.thumbnail_url) {
        next.thumbnail_image = uploadedFile;
      } else if (removedThumbnail) {
        next.thumbnail_image = null;
        next.thumbnail_url = "";
      }
      setForm(next);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setNotice("Category details saved.");
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="products-page">
      {notice && <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <form className="products-page__editor" onSubmit={handleSubmit}>
        <div className="products-page__heading">
          <div>
            <h2>{isEdit ? "Edit category" : "New category"}</h2>
            <p>Define a category used in the product catalogue.</p>
          </div>
          <div className="products-page__heading-actions">
            <button className="ui-button" type="button" onClick={() => navigate("/admin/products/categories")}>
              Cancel
            </button>
            <button className="ui-button ui-button--primary" type="submit" disabled={saving || loading}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="products-page__loading">Loading category...</div>
        ) : (
          <div className="products-page__layout">
            <section className="products-page__card products-page__form">
              <label>
                English name
                <input
                  value={form.name}
                  required
                  autoFocus
                  placeholder="e.g. Steel Bars"
                  onChange={(event) => updateField("name", event.target.value)}
                />
              </label>
              <label>
                Chinese name
                <input
                  value={form.name_zh}
                  placeholder="e.g. 钢筋"
                  onChange={(event) => updateField("name_zh", event.target.value)}
                />
              </label>
              <label>
                Slug
                <input
                  value={form.slug}
                  placeholder="e.g. steel-bars"
                  onChange={(event) => updateField("slug", event.target.value)}
                />
              </label>
              <label>
                Status
                <select
                  value={form.status ? "1" : "0"}
                  onChange={(event) => updateField("status", event.target.value === "1")}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </label>
              <label>
                Display order
                <input
                  type="number"
                  min="0"
                  value={form.sort_order}
                  onChange={(event) => updateField("sort_order", event.target.value)}
                />
              </label>
              <div className="products-page__field">
                <span>Show in navigation</span>
                <div className="products-page__radios">
                  <label>
                    <input
                      type="radio"
                      name="show_in_navigation_menu"
                      checked={form.show_in_navigation_menu}
                      onChange={() => updateField("show_in_navigation_menu", true)}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="show_in_navigation_menu"
                      checked={!form.show_in_navigation_menu}
                      onChange={() => updateField("show_in_navigation_menu", false)}
                    />
                    No
                  </label>
                </div>
              </div>
            </section>

            <aside className="products-page__side">
              <section className="products-page__card products-page__panel">
                <header>
                  <p>THUMBNAIL</p>
                  <h3>Upload image</h3>
                </header>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  onChange={onThumbnailChange}
                  hidden
                />
                {thumbnailPreview || form.thumbnail_url ? (
                  <div className="products-page__upload products-page__upload--preview">
                    <img key={thumbnailPreview || form.thumbnail_url} src={thumbnailPreview || form.thumbnail_url} alt="" />
                    <button
                      className="products-page__upload-remove"
                      type="button"
                      aria-label="Remove image"
                      onClick={clearThumbnail}
                    >
                      <Icon icon="ph:x" height={18} />
                    </button>
                  </div>
                ) : (
                  <button
                    className="products-page__upload"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className="products-page__upload-icon"><Icon icon="ph:plus" height={18} /></span>
                    <strong>Upload thumbnail</strong>
                    <small>JPG, PNG or WebP, max 5 MB.</small>
                  </button>
                )}
              </section>
            </aside>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProductCategoryForm;
